import React, { useState, useCallback } from 'react';
import styles from './share-bar.module.scss';
import { SVGS } from '@zonofi/zono-money-design-system';
import {
  getImageBlobFromLambda,
  ImageItem,
} from '../folder-list/hooks/use-folder-list';
import { useLocation } from 'react-router-dom';

const SHARE_TITLE = 'Shared from ZoTok Catalogue';

interface ShareBarProps {
  selectedImages: ImageItem[];
}

export const ShareBar: React.FC<ShareBarProps> = ({ selectedImages }) => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const domainName =
    typeof window !== 'undefined'
      ? window.location.hostname
      : (location?.pathname ?? '');

  const isQa = domainName.includes('-qa') || !domainName.includes('mobile');

  const shareImageToWhatsapp = useCallback(
    async (images: ImageItem[]) => {
      try {
        // Extract S3 URLs from image objects
        const s3Urls = images.map((img) => img.src);

        // Fetch all image blobs in parallel
        const blobPromises = s3Urls.map((url) =>
          getImageBlobFromLambda(url, isQa)
        );
        const blobs = await Promise.all(blobPromises);

        // Create array of objects with file, name, and linkUrl
        const filesWithLinks = blobs.map((blob, index) => {
          const extension = blob.type?.split('/')[1] || 'jpg';
          const filename = `${images[index].id}.${extension}`;
          const file = new File([blob], filename, {
            type: blob.type || 'image/jpeg',
          });
          return {
            file: file,
            name: images[index].name || '',
            linkUrl: images[index].linkUrl || '',
          };
        });

        const files = filesWithLinks.map((item) => item.file);

        // Create detailed text with name and linkUrl for each item
        const shareText =
          filesWithLinks?.length === 1
            ? `${filesWithLinks?.[0]?.name}: ${filesWithLinks?.[0]?.linkUrl}`
            : filesWithLinks
                .filter((item) => item.linkUrl) // Remove items without links
                .map((item) => `${item.name}: ${item.linkUrl}`)
                .join('\n');

        await navigator.share({
          files: files,
          text: `Check out these products:\n${shareText}`,
          title: `Check out these products:\n${shareText}`,
        });

        return true;
      } catch (err) {
        console.error('Share failed:', err);
        return false;
      }
    },
    [isQa]
  );

  const shareOptions = useCallback(async () => {
    if (isLoading || selectedImages.length === 0) {
      return;
    }
    setIsLoading(true);
    try {
      await shareImageToWhatsapp(selectedImages);
    } catch (error) {
      console.error('WhatsApp share failed', error);
      // openWhatsAppFallback();
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, selectedImages, shareImageToWhatsapp]);

  const handleShareClick = useCallback(async () => {
    if (isLoading || selectedImages.length === 0) {
      return;
    }
    shareOptions();
  }, [isLoading, selectedImages.length, shareOptions]);

  return (
    <div className="catalog-tool">
      <div className={styles.shareBar}>
        <button
          className={styles.shareCta}
          onClick={handleShareClick}
          disabled={isLoading}
          aria-label={`Share ${selectedImages.length} ${
            selectedImages.length === 1 ? 'Image' : 'Images'
          }`}
        >
          <img src={SVGS.ShareIcon} className={styles.shareIcon} alt="Share" />
          <span style={{ fontFamily: 'Segoe-UI-VF' }}>
            {isLoading
              ? 'Preparing...'
              : `Share ${selectedImages.length} ${
                  selectedImages.length === 1 ? 'Image' : 'Images'
                }`}
          </span>
        </button>
      </div>
    </div>
  );
};
